import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Rss } from "lucide-react";
import { listUpdates } from "../lib/api";
import { apiOrigin } from "../lib/apiBase";
import {
    CATEGORIES, chipClasses, dotClasses, formatDate, getCategory,
} from "../lib/updates";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import CountdownStrip from "../components/CountdownStrip";

export default function UpdatesIndexPage() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeCat, setActiveCat] = useState(null);

    useEffect(() => {
        document.title = "Updates — Cleany.dk";
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        listUpdates({ category: activeCat || undefined })
            .then((data) => { if (!cancelled) setPosts(data); })
            .catch(() => { if (!cancelled) setPosts([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [activeCat]);

    const featured = posts[0];
    const rest = posts.slice(1);

    return (
        <>
            <CountdownStrip onCtaClick={() => window.dispatchEvent(new CustomEvent("cleany:open-waitlist", { detail: { source: "updates" } }))} />
            <Navbar onCtaClick={() => window.dispatchEvent(new CustomEvent("cleany:open-waitlist", { detail: { source: "updates-navbar" } }))} />

            <main className="bg-slate-50/50 pb-20">
                {/* Hero */}
                <section className="bg-white border-b border-slate-200">
                    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-14">
                        <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 border border-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
                            <span className="h-1.5 w-1.5 rounded-full bg-sky-500" /> Updates
                        </div>
                        <h1 className="mt-4 font-display text-4xl md:text-6xl font-bold tracking-tight text-slate-900 max-w-3xl">
                            Nyheder fra <span className="text-gradient-blue">Cleany</span>.
                        </h1>
                        <p className="mt-4 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
                            Produktopdateringer, branche-tips, kundehistorier og guides — fra teamet bag Cleany.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-2" data-testid="updates-filter-pills">
                            <FilterPill
                                label="Alle"
                                active={!activeCat}
                                onClick={() => setActiveCat(null)}
                                testid="updates-pill-all"
                            />
                            {CATEGORIES.map((c) => (
                                <FilterPill
                                    key={c.slug}
                                    label={c.label}
                                    active={activeCat === c.slug}
                                    onClick={() => setActiveCat(c.slug)}
                                    testid={`updates-pill-${c.slug}`}
                                />
                            ))}
                            <a
                                href={`${apiOrigin}/api/updates/rss.xml`}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="updates-rss"
                                className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-slate-600 hover:text-orange-600 hover:border-orange-300 px-3 h-9 text-xs font-semibold transition-colors"
                            >
                                <Rss className="h-3.5 w-3.5" />
                                RSS feed
                            </a>
                        </div>
                    </div>
                </section>

                {/* Posts */}
                <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-12">
                    {loading ? (
                        <div className="text-center py-20 text-slate-500" data-testid="updates-loading">
                            Indlæser…
                        </div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-20 text-slate-500" data-testid="updates-empty">
                            Ingen indlæg i denne kategori endnu — kig forbi igen senere.
                        </div>
                    ) : (
                        <>
                            {featured && <FeaturedCard post={featured} />}
                            {rest.length > 0 && (
                                <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="updates-grid">
                                    {rest.map((p, i) => (
                                        <PostCard key={p.slug} post={p} index={i} />
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </section>
            </main>

            <Footer onCtaClick={() => window.dispatchEvent(new CustomEvent("cleany:open-waitlist", { detail: { source: "updates-footer" } }))} />
        </>
    );
}

/* ---------- pieces ---------- */
function FilterPill({ label, active, onClick, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testid}
            className={
                "inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-xs font-semibold transition-all " +
                (active
                    ? "bg-slate-900 text-white shadow-sm"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-slate-300 hover:bg-slate-50")
            }
        >
            {label}
        </button>
    );
}

function CategoryChip({ slug }) {
    const c = getCategory(slug);
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${chipClasses(c.color)}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${dotClasses(c.color)}`} />
            {c.label}
        </span>
    );
}

function FeaturedCard({ post }) {
    return (
        <Link
            to={`/updates/${post.slug}`}
            data-testid="updates-featured"
            className="group block rounded-3xl bg-white border border-slate-200 overflow-hidden hover:border-sky-300 hover:shadow-xl shadow-slate-200/40 transition-all"
        >
            <div className="grid md:grid-cols-5 gap-0">
                <div className="md:col-span-3 aspect-[16/9] md:aspect-auto overflow-hidden bg-slate-100">
                    {post.cover && (
                        <img
                            src={post.cover}
                            alt={post.title}
                            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
                        />
                    )}
                </div>
                <div className="md:col-span-2 p-7 md:p-10 flex flex-col justify-center">
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                        <CategoryChip slug={post.category} />
                        <span>{formatDate(post.date)}</span>
                        <span>· {post.reading_time} min</span>
                    </div>
                    <h2 className="mt-4 font-display text-2xl md:text-3xl font-bold tracking-tight text-slate-900 group-hover:text-sky-700 transition-colors">
                        {post.title}
                    </h2>
                    <p className="mt-3 text-sm md:text-base text-slate-600 leading-relaxed line-clamp-3">
                        {post.excerpt}
                    </p>
                    <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700">
                        Læs indlæg
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                </div>
            </div>
        </Link>
    );
}

function PostCard({ post, index }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: index * 0.06 }}
        >
            <Link
                to={`/updates/${post.slug}`}
                data-testid={`updates-card-${post.slug}`}
                className="group block h-full rounded-2xl bg-white border border-slate-200 overflow-hidden hover:border-sky-300 hover:-translate-y-1 hover:shadow-lg transition-all"
            >
                <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                    {post.cover && (
                        <img
                            src={post.cover}
                            alt={post.title}
                            loading="lazy"
                            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                        />
                    )}
                </div>
                <div className="p-5">
                    <div className="flex items-center gap-2 text-[11px] text-slate-500">
                        <CategoryChip slug={post.category} />
                        <span>{formatDate(post.date)}</span>
                    </div>
                    <h3 className="mt-3 font-display text-lg font-bold text-slate-900 group-hover:text-sky-700 transition-colors line-clamp-2 leading-snug">
                        {post.title}
                    </h3>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed line-clamp-3">
                        {post.excerpt}
                    </p>
                    <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-sky-700">
                        Læs mere
                        <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
                    </div>
                </div>
            </Link>
        </motion.div>
    );
}
